import React, {memo, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {NamedProps} from 'react-select/src/Select';
import {UiDropdown, UiDropdownProps} from '~/components/elements';
import {useDebounce} from '~/hooks';
import {OPTION_ALL, OPTION_ALL_UNIQ_VALUE, useDropdown} from '~/hooks/useDropdown';
import {AppDispatch} from '~/redux/root-store';
import {SOURCES_SCHOOL} from '~/redux/schools/entity-config';
import {getSchoolsDropdown} from '~/redux/schools/slice';
import {fetchSchools} from '~/redux/schools/thunk';
import {SchoolEntity} from '~/redux/schools/types';

interface Props extends Omit<UiDropdownProps, 'defaultValue'> {
  onChange: NamedProps['onChange'];
  includeAll?: boolean;
  multiple?: any;
  onSelect?: (value?: string | number) => any;

  [key: string]: any;
}

const SchoolsDropdown = ({defaultValue, includeAll = false, onChange, onSelect, multiple, ...rest}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const {data: schools, paginator, loading} = useSelector(getSchoolsDropdown);

  const {searchQuery, onInputChange, options} = useDropdown<SchoolEntity>({
    defaultValue,
    multiple,
    includeAll,
    onSelect,
    optionData: schools,
    getLabel: i => i.name || '',
  });

  const query = useDebounce(searchQuery, 300);

  const getSchools = useCallback(
    async (isLoadMore = false, page = 1) => {
      await dispatch(
        fetchSchools({
          source: SOURCES_SCHOOL.SCHOOLS_DROPDOWN,
          page,
          isLoadMore,
          name: query,
          limit: 30,
        }),
      );
    },
    [dispatch, query],
  );

  const loadMoreSchools = async () => {
    if (paginator?.next_page && !loading) {
      await getSchools(true, paginator.next_page);
    }
  };

  useEffect(() => {
    getSchools();
  }, [getSchools]);

  const onChangeLocal: NamedProps['onChange'] = (value, action) => {
    if (!onChange) {
      return null;
    }
    if (value?.value === OPTION_ALL_UNIQ_VALUE) {
      onChange({value: '', label: 'All'}, action);
    } else {
      onChange(value, action);
    }
  };

  return (
    <UiDropdown
      defaultValue={options.find(o => o.value == defaultValue) || (includeAll ? OPTION_ALL : undefined)}
      options={options}
      onInputChange={onInputChange}
      onChange={onChangeLocal}
      onMenuScrollToBottom={loadMoreSchools}
      isLoading={loading}
      {...rest}
    />
  );
};

export default memo(SchoolsDropdown);
